import { useMemo, useRef, useState } from 'react';
import { BookOpen, Check, Copy, Download, FileText, Loader2, Plus, Trash2, Upload } from 'lucide-react';
import {
    useTemplates,
    useUploadTemplate,
    useDeleteTemplate,
    usePlaceholderCatalog,
    downloadTemplateFile,
    type PlaceholderCatalog,
    type TpTemplate,
} from '@/api/tpTemplates';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Alert } from '@/components/ui/Alert';
import { FormField, Input } from '@/components/ui/FormField';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';

function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateTime(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
    });
}

export function TemplatesPanel() {
    const canManage = useAuthStore((s) => s.hasPermission('manage_settings'));
    const { data, isLoading, isError, error } = useTemplates(canManage);
    const deleteM = useDeleteTemplate();

    const [uploading, setUploading] = useState(false);
    const [showCatalog, setShowCatalog] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState<TpTemplate | null>(null);
    const [downloadErr, setDownloadErr] = useState<string | null>(null);

    async function handleDownload(row: TpTemplate) {
        setDownloadErr(null);
        try {
            const { blob, filename } = await downloadTemplateFile(row.id, row.fileName);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            setDownloadErr(extractErrorMessage(e, 'Download failed.'));
        }
    }

    if (!canManage) {
        return (
            <Alert variant="info">
                You don't have permission to manage treatment-plan templates.
            </Alert>
        );
    }

    return (
        <div>
            <div className="flex items-start justify-between gap-6 mb-3">
                <div className="max-w-[720px] space-y-2 text-xs text-text-subtle">
                    <p>
                        Upload .docx templates used for exporting treatment plans. Placeholders
                        in the template are filled in at export time.
                    </p>
                    <p>
                        Fields use <code>{'{name}'}</code>, loops use{' '}
                        <code>{'{#name}'}…{'{/name}'}</code>, and images use{' '}
                        <code>{'{%name}'}</code>.
                    </p>
                    <p>
                        Incorrect placeholders render as{' '}
                        <code>[Invalid placeholder: …]</code> in the export.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => setShowCatalog(true)}>
                        <BookOpen className="w-3.5 h-3.5" /> Placeholder reference
                    </Button>
                    <Button variant="primary" size="sm" onClick={() => setUploading(true)}>
                        <Plus className="w-3.5 h-3.5" /> Upload template
                    </Button>
                </div>
            </div>

            {downloadErr && (
                <Alert variant="danger" className="mt-3 text-xs">{downloadErr}</Alert>
            )}

            <Card className="p-0 overflow-hidden mt-3">
                {isLoading ? (
                    <div className="p-12 flex items-center justify-center text-text-subtle">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                ) : isError ? (
                    <div className="p-12 text-center text-error">
                        <p className="font-medium mb-1">Couldn't load templates</p>
                        <p className="text-xs text-text-subtle">
                            {extractErrorMessage(error, 'Please try again.')}
                        </p>
                    </div>
                ) : !data || data.length === 0 ? (
                    <div className="p-12 text-center text-text-subtle">
                        <FileText className="w-6 h-6 mx-auto mb-2 opacity-40" />
                        <p>No templates yet.</p>
                        <p className="text-xs mt-1">
                            Upload a .docx file to get started.
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    <th className="px-3 py-2 font-bold">Name</th>
                                    <th className="px-3 py-2 font-bold">File</th>
                                    <th className="px-3 py-2 font-bold w-20">Size</th>
                                    <th className="px-3 py-2 font-bold">Uploaded by</th>
                                    <th className="px-3 py-2 font-bold">Uploaded</th>
                                    <th className="px-3 py-2 font-bold w-28 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((r) => (
                                    <tr key={r.id} className="border-b border-border last:border-b-0 hover:bg-surface-muted transition-colors">
                                        <td className="px-3 py-2 font-medium">
                                            <span className="inline-flex items-center gap-2">
                                                {r.name}
                                                {r.isSystem && <Badge variant="info">System</Badge>}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-text-muted">{r.fileName}</td>
                                        <td className="px-3 py-2 text-text-muted tabular-nums whitespace-nowrap">
                                            {formatSize(r.fileSize)}
                                        </td>
                                        <td className="px-3 py-2 text-text-muted whitespace-nowrap">
                                            {r.uploadedByName || '—'}
                                        </td>
                                        <td className="px-3 py-2 text-text-muted whitespace-nowrap">
                                            {formatDateTime(r.uploadedAt)}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <Button
                                                variant="ghost"
                                                size="xs"
                                                aria-label={`Download ${r.name}`}
                                                onClick={() => handleDownload(r)}
                                                className="mr-1"
                                            >
                                                <Download className="w-3 h-3" />
                                            </Button>
                                            {!r.isSystem && (
                                                <Button
                                                    variant="ghost"
                                                    size="xs"
                                                    aria-label={`Delete ${r.name}`}
                                                    onClick={() => setConfirmDelete(r)}
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {uploading && (
                <UploadTemplateDialog onClose={() => setUploading(false)} />
            )}

            {showCatalog && (
                <PlaceholderCatalogDialog onClose={() => setShowCatalog(false)} />
            )}

            {confirmDelete && (
                <Dialog open onOpenChange={(o) => !o && setConfirmDelete(null)}>
                    <DialogContent aria-describedby={undefined}>
                        <DialogHeader>
                            <DialogTitle>Delete template?</DialogTitle>
                        </DialogHeader>
                        <p className="text-sm text-text-muted">
                            Delete <span className="font-medium text-text">{confirmDelete.name}</span>?
                            This removes the template file permanently; previously exported .docx
                            documents are unaffected.
                        </p>
                        <DialogFooter>
                            <DialogClose asChild>
                                <Button variant="ghost" size="sm" type="button">Cancel</Button>
                            </DialogClose>
                            <Button
                                variant="danger"
                                size="sm"
                                disabled={deleteM.isPending}
                                onClick={async () => {
                                    await deleteM.mutateAsync(confirmDelete.id);
                                    setConfirmDelete(null);
                                }}
                            >
                                {deleteM.isPending ? 'Deleting…' : 'Delete'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}

// ── Placeholder reference ──────────────────────────────────────────────
// Scrollable catalog of every placeholder the template engine understands.
// Click any chip to copy the exact tag (e.g. `{plan.title}`, `{#behaviors}`,
// `{%plan.bcbaSignatureImage}`) to the clipboard. Custom fields come from
// the server — non-archived client and tx_plan custom fields are split into
// their own sections so authors can tell which scope each placeholder resolves against.
function PlaceholderCatalogDialog({ onClose }: { onClose: () => void }) {
    const { data, isLoading, isError, error } = usePlaceholderCatalog(true);
    const [query, setQuery] = useState('');

    const q = query.trim().toLowerCase();
    const matches = useMemo(() => {
        if (!data) return null;
        if (!q) return data;
        // Filter recursively; keep the section if any child matches.
        const matchField = (k: string, d: string) =>
            k.toLowerCase().includes(q) || d.toLowerCase().includes(q);
        const filtered: PlaceholderCatalog = {
            scalars: data.scalars.filter((s) => matchField(s.key, s.description)),
            images: data.images.filter((i) => matchField(i.key, i.description)),
            customFields: data.customFields.filter((c) => matchField(c.key, c.description)),
            loops: data.loops
                .map((loop) => ({
                    ...loop,
                    fields: loop.fields.filter((f) => matchField(f.key, f.description)),
                    nested: loop.nested?.map((n) => ({
                        ...n,
                        fields: n.fields.filter((f) => matchField(f.key, f.description)),
                        images: n.images?.filter((i) => matchField(i.key, i.description)),
                    })),
                }))
                .filter((loop) => {
                    const selfMatch = matchField(loop.key, loop.description);
                    const fieldMatch = loop.fields.length > 0;
                    const nestedMatch = (loop.nested ?? []).some((n) =>
                        n.fields.length > 0 || (n.images && n.images.length > 0),
                    );
                    return selfMatch || fieldMatch || nestedMatch;
                }),
        };
        return filtered;
    }, [data, q]);

    // Group fields by section. Plan-level signature images live in the
    // Treatment Plan group so BCBAs don't have to hunt for them in a
    // separate "Images" list.
    type FieldRow = { tag: string; description: string };
    const fieldsByGroup = useMemo((): Array<{ group: string; rows: FieldRow[] }> => {
        if (!matches) return [];
        const groups = new Map<string, FieldRow[]>();
        for (const s of matches.scalars) {
            const g = groups.get(s.group) ?? [];
            g.push({ tag: `{${s.key}}`, description: s.description });
            groups.set(s.group, g);
        }
        for (const i of matches.images) {
            if (!i.key.startsWith('plan.')) continue;
            const g = groups.get('Treatment Plan') ?? [];
            g.push({ tag: `{%${i.key}}`, description: i.description });
            groups.set('Treatment Plan', g);
        }
        return Array.from(groups.entries()).map(([group, rows]) => ({ group, rows }));
    }, [matches]);

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                aria-describedby={undefined}
                className="max-w-5xl h-[720px] flex flex-col"
            >
                <DialogHeader className="pb-3">
                    <DialogTitle>Placeholder reference</DialogTitle>
                    <div className="pt-3">
                        <Input
                            type="search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Filter by key or description…"
                            aria-label="Filter placeholders"
                        />
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto pr-1">
                    {isLoading && (
                        <div className="py-8 flex items-center justify-center text-text-subtle">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                        </div>
                    )}
                    {isError && (
                        <Alert variant="danger">
                            {extractErrorMessage(error, 'Could not load placeholders.')}
                        </Alert>
                    )}
                    {matches && (
                        <div className="divide-y divide-border">
                            {fieldsByGroup.map(({ group, rows }) => (
                                <CatalogSection key={group} title={group}>
                                    <FieldGrid rows={rows} />
                                </CatalogSection>
                            ))}

                            {(() => {
                                const clientCf = matches.customFields.filter((c) => c.key.startsWith('client.cf.'));
                                const planCf   = matches.customFields.filter((c) => c.key.startsWith('plan.cf.'));
                                return (
                                    <>
                                        {clientCf.length > 0 && (
                                            <CatalogSection title="Client custom fields">
                                                <p className="text-xs text-text-subtle mb-3">
                                                    Placeholders are auto-generated from each custom field's
                                                    label. Renaming a custom field changes its placeholder —
                                                    existing templates will need updating.
                                                </p>
                                                <FieldGrid
                                                    rows={clientCf.map((c) => ({
                                                        tag: `{${c.key}}`,
                                                        description: c.label,
                                                    }))}
                                                />
                                            </CatalogSection>
                                        )}
                                        {planCf.length > 0 && (
                                            <CatalogSection title="Treatment plan custom fields">
                                                <p className="text-xs text-text-subtle mb-3">
                                                    Per-plan custom fields — values live on each treatment plan,
                                                    not on the client. Configure under Settings → Custom Fields
                                                    → Treatment Plan Fields.
                                                </p>
                                                <FieldGrid
                                                    rows={planCf.map((c) => ({
                                                        tag: `{${c.key}}`,
                                                        description: c.label,
                                                    }))}
                                                />
                                            </CatalogSection>
                                        )}
                                    </>
                                );
                            })()}

                            {matches.loops.map((loop) => (
                                <CatalogSection key={loop.key} title={`Loop: ${loop.key}`}>
                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                        <CopyChip text={`{#${loop.key}}`} />
                                        <span className="text-xs text-text-subtle">…</span>
                                        <CopyChip text={`{/${loop.key}}`} />
                                    </div>
                                    <p className="text-xs text-text-muted mb-3">{loop.description}</p>
                                    {loop.fields.length > 0 && (
                                        <FieldGrid rows={loop.fields.map((f) => ({ tag: `{${f.key}}`, description: f.description }))} />
                                    )}
                                    {loop.nested?.map((n) => (
                                        <div key={n.key} className="mt-4 pl-3 border-l-2 border-border">
                                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                                <CopyChip text={`{#${n.key}}`} />
                                                <span className="text-xs text-text-subtle">…</span>
                                                <CopyChip text={`{/${n.key}}`} />
                                            </div>
                                            <p className="text-xs text-text-muted mb-3">{n.description}</p>
                                            <FieldGrid
                                                rows={[
                                                    ...n.fields.map((f) => ({ tag: `{${f.key}}`, description: f.description })),
                                                    ...(n.images ?? []).map((i) => ({ tag: `{%${i.key}}`, description: i.description })),
                                                ]}
                                            />
                                        </div>
                                    ))}
                                </CatalogSection>
                            ))}

                            {q &&
                                fieldsByGroup.length === 0 &&
                                matches.customFields.length === 0 &&
                                matches.loops.length === 0 && (
                                    <div className="py-8 text-center text-text-subtle">
                                        <p>No placeholders match "{q}".</p>
                                    </div>
                                )}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Close</Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CatalogSection({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section className="py-4 first:pt-2 last:pb-2">
            <h3 className="font-display text-sm font-bold text-text mb-3">{title}</h3>
            {children}
        </section>
    );
}

function FieldGrid({ rows }: { rows: Array<{ tag: string; description: string }> }) {
    return (
        <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 items-center">
            {rows.map((r) => (
                <FieldRow key={r.tag} tag={r.tag} description={r.description} />
            ))}
        </div>
    );
}

function FieldRow({ tag, description }: { tag: string; description: string }) {
    return (
        <>
            <CopyChip text={tag} />
            <span className="text-text-muted text-xs">{description}</span>
        </>
    );
}

function CopyChip({ text }: { text: string }) {
    const [copied, setCopied] = useState(false);
    async function handleCopy() {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1400);
        } catch {
            // clipboard unavailable — no-op; user can select manually
        }
    }
    return (
        <button
            type="button"
            onClick={handleCopy}
            aria-label={`Copy ${text}`}
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-border bg-surface-muted text-xs font-mono text-text hover:border-accent hover:bg-surface transition-colors"
        >
            <code>{text}</code>
            {copied ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3 text-text-subtle" />}
        </button>
    );
}

function UploadTemplateDialog({ onClose }: { onClose: () => void }) {
    const uploadM = useUploadTemplate();
    const fileRef = useRef<HTMLInputElement>(null);
    const [name, setName] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [err, setErr] = useState<string | null>(null);

    function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
        const f = e.target.files?.[0] ?? null;
        setFile(f);
        // Default the name field to the filename sans extension
        if (f && !name) {
            setName(f.name.replace(/\.docx$/i, ''));
        }
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        if (!file) { setErr('Pick a .docx file.'); return; }
        if (!/\.docx$/i.test(file.name)) {
            setErr('Only .docx files are accepted.');
            return;
        }
        try {
            await uploadM.mutateAsync({ file, name: name.trim() || undefined });
            onClose();
        } catch (e2) {
            const anyErr = e2 as { response?: { data?: { error?: string } } };
            setErr(anyErr?.response?.data?.error || (extractErrorMessage(e2, 'Upload failed.')));
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && !uploadM.isPending && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Upload template</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-3">
                    <FormField label="Template name">
                        {(id) => (
                            <Input
                                id={id}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Defaults to the filename"
                                disabled={uploadM.isPending}
                            />
                        )}
                    </FormField>
                    <div>
                        <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-1.5">
                            File <span className="text-error">*</span>
                        </p>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                ref={fileRef}
                                type="file"
                                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                onChange={handleFileChange}
                                disabled={uploadM.isPending}
                                className="sr-only"
                            />
                            <Button
                                variant="ghost"
                                size="sm"
                                type="button"
                                onClick={() => fileRef.current?.click()}
                                disabled={uploadM.isPending}
                            >
                                <Upload className="w-3.5 h-3.5" /> Choose .docx
                            </Button>
                            <span className="text-xs text-text-muted truncate">
                                {file ? `${file.name} · ${formatSize(file.size)}` : 'No file selected'}
                            </span>
                        </label>
                        <p className="text-xs text-text-subtle mt-1.5">
                            Max 10 MB. Only Word .docx files are accepted.
                        </p>
                    </div>
                    {err && <Alert variant="danger" className="text-xs">{err}</Alert>}
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" type="button" disabled={uploadM.isPending}>
                                Cancel
                            </Button>
                        </DialogClose>
                        <Button
                            variant="primary"
                            size="sm"
                            type="submit"
                            disabled={uploadM.isPending || !file}
                        >
                            {uploadM.isPending ? 'Uploading…' : 'Upload'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
